import {inject, injectable} from 'inversify';

import type {
  ClientTaskApiModel,
  CoachOrClientTaskApiModel,
  CoachTaskApiModel,
  TaskExpandedCommon,
} from '../../../../api/task';
import type {IApiDataMapper} from '../../../ports/types';
import type {TaskModel} from '../Task.model';

import {getEntityTypeById} from '../../../../utils/entity/getEntityType';
import {CurrentUserService} from '../../../system/currentUser/CurrentUser.service';
import {CourseMapperJrpc} from '../../Course/mappers/Course.mapper.jrpc';
import {DateTimeModelFactory} from '../../Date/DateTime.factory';
import {TaskFactory} from '../Task.factory';
import {TaskExpandJrpc} from './Task.expand.jrpc';

@injectable()
export class TaskMapperJrpc
  implements IApiDataMapper<TaskMapperJrpc.ApiData, TaskModel.Props, TaskModel>
{
  constructor(
    @inject(TaskFactory)
    private readonly factory: TaskFactory,
    @inject(TaskExpandJrpc)
    public readonly expand: TaskExpandJrpc,
    @inject(CourseMapperJrpc) private readonly courseMapper: CourseMapperJrpc,
    @inject(CurrentUserService)
    private readonly currentUserService: CurrentUserService,
    @inject(DateTimeModelFactory)
    private readonly dateTimeModelFactory: DateTimeModelFactory,
  ) {}

  mapDataToModel(apiData: TaskMapperJrpc.ExpandedApiData): TaskModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(apiData: TaskMapperJrpc.ExpandedApiData): TaskModel.Props {
    const id = apiData._id;
    const title = this.title(apiData);
    const description = this.description(apiData);
    const coachTaskId = this.coachTaskId(apiData);
    const clientId = this.clientId(apiData);
    const isMeCoach = this.isMeCoach(apiData);
    const isClientTask = this.isClientTask(apiData);
    const isCoachTask = this.isCoachTask(apiData);
    const isBaseTask = this.isBaseTask(apiData);
    const baseTaskId = this.baseTaskId(apiData);
    const uuid = this.uuid(apiData);
    const startDate = this.startDate(apiData);
    const endDate = this.endDate(apiData);
    const course = this.course(apiData);
    const status = this.status(apiData);

    return {
      apiData: {...apiData},
      baseTaskId,
      clientId,
      coachTaskId,
      course,
      description,
      endDate,
      id,
      isBaseTask,
      isClientTask,
      isCoachTask,
      isMeCoach,
      startDate,
      status,
      title,
      uuid,
    };
  }

  private baseTaskId(
    props: TaskMapperJrpc.ApiData,
  ): TaskModel.Props['baseTaskId'] {
    return props.base_task_id;
  }

  private clientId(props: TaskMapperJrpc.ApiData): TaskModel.Props['clientId'] {
    return (props as ClientTaskApiModel).user_id ?? '';
  }

  private coachTaskId(
    props: TaskMapperJrpc.ApiData,
  ): TaskModel.Props['coachTaskId'] {
    return (props as ClientTaskApiModel).coach_task_id ?? '';
  }

  private course(
    props: TaskMapperJrpc.ExpandedApiData,
  ): TaskModel.Props['course'] {
    const course = props.course;

    if (!course) {
      return null;
    }

    return this.courseMapper.mapDataToModel(course);
  }

  private description(
    props: TaskMapperJrpc.ApiData,
  ): TaskModel.Props['description'] {
    return props.description ?? '';
  }

  private endDate(props: TaskMapperJrpc.ApiData): TaskModel.Props['endDate'] {
    return this.dateTimeModelFactory.createFromDateTimeObject(props.end_date);
  }

  private isBaseTask(
    props: TaskMapperJrpc.ApiData,
  ): TaskModel.Props['isBaseTask'] {
    return getEntityTypeById(props?._id) === 'base_task';
  }

  private isClientTask(
    props: TaskMapperJrpc.ApiData,
  ): TaskModel.Props['isClientTask'] {
    return getEntityTypeById(props?._id) === 'task';
  }

  private isCoachTask(
    props: TaskMapperJrpc.ApiData,
  ): TaskModel.Props['isCoachTask'] {
    return getEntityTypeById(props?._id) === 'coach_task';
  }

  private isMeCoach(
    props: TaskMapperJrpc.ApiData,
  ): TaskModel.Props['isMeCoach'] {
    return this.currentUserService.isCoachForEntity(props);
  }

  private startDate(
    props: TaskMapperJrpc.ApiData,
  ): TaskModel.Props['startDate'] {
    return this.dateTimeModelFactory.createFromDateTimeObject(props.start_date);
  }

  private status(props: TaskMapperJrpc.ApiData): TaskModel.Props['status'] {
    return props.status!; // is it always defined?
  }

  private title(props: TaskMapperJrpc.ApiData): TaskModel.Props['title'] {
    return props.title ?? '';
  }

  private uuid(props: TaskMapperJrpc.ApiData): TaskModel.Props['uuid'] {
    return props.uuid;
  }
}

export namespace TaskMapperJrpc {
  export type ApiData = CoachOrClientTaskApiModel;

  export type ClientApiData = ClientTaskApiModel;

  export type CoachApiData = CoachTaskApiModel;

  export type ExpandedApiData = TaskExpandedCommon;

  export type ExpandedApiDataClient = ClientTaskApiModel &
    TaskExpandJrpc.ExpandedFieldsClient;

  export type ExpandedApiDataCoach = CoachTaskApiModel &
    TaskExpandJrpc.ExpandedFieldsCoach;
}
